import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import {
  updateDoc,
  doc,
  serverTimestamp,
  addDoc,
  collection,
  onSnapshot,
  query,
  setDoc,
  deleteDoc,
  orderBy,
  getDocs,
  getDoc,
  where
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useStoreSelector } from '../../redux/store';
import { consoleLog } from '../../utils/Logger';
import useAuthenticated from '../authenticated';
import { injectSquirrelly } from './utils';

/*
  Pages are designed from the /pages collection and published to either /pages_public or /pages_private
  This hook specifially loads the page from /pages whereas the usePage hook works with the runtime version of the page from either /page_public or /pages_private
*/

const usePageDesigner = (props = {}) => {
  const [searchParams] = useSearchParams();
  const { types } = useSelector(state => state.Content);

  const { settings } = useSelector(state => state.Preloaded);
  const { state: pages, loaded: pagesLoaded } = useStoreSelector(
    state => state.Preloaded.pages,
    {
      source: `usePageDesigner ${props?.source || ''}`
      //ignore: pageId === undefined
    }
  );

  const { dataLinks: storeDataLinks } = useSelector(state => state.DataLinks);

  const { state: dataLinks, loaded: dataLinksLoaded } = useStoreSelector(
    state => state.DataLinks.dataLinks,
    {
      source: `usePageDesigner ${props?.source || ''}`
    }
  );

  consoleLog('usePageDesigner.dataLinks', { dataLinks, dataLinksLoaded });

  const [showLibrary, setShowLibrary] = useState(false);
  const [showSnippets, setShowSnippets] = useState(false);
  const [showSequences, setShowSequences] = useState(false);
  const [previewPage, setPreviewPage] = useState(false);
  const [selectedSnippetOrTemplate, setSelectedSnippetOrTemplate] = useState();
  const [showPageRevisions, setShowPageRevisions] = useState(false);
  const [
    showSnippetOrTemplateRevisions,
    setShowSnippetOrTemplateRevisions
  ] = useState(false);
  const [pageRevisions, setPageRevisions] = useState([]);
  const [templateOrSnippetRevisions, setTemplateOrSnippetRevisions] = useState(
    []
  );
  const [pageTemplate, setPageTemplate] = useState();
  const [sequences, setSequences] = useState([]);
  const [snippetOrTemplateSequences, setSnippetOrTemplateSequences] = useState(
    []
  );
  const [page, setPage] = useState();
  const [pageDataLinks, setPageDataLinks] = useState([]);
  const [is404, setIs404] = useState(false);
  const [sequencesLoaded, setSequencesLoaded] = useState(false);
  const [
    snippetOrTemplateSequencesLoaded,
    setSnippetOrTemplateSequencesLoaded
  ] = useState(false);
  const [lastPageId, setLastPageId] = useState();
  const [lastSnippetOrTemplateId, setLastSnippetOrTemplateId] = useState();

  const user = useSelector(state => state.User);

  const pageContentType = types.find(t => t.type === 'page');
  const pageTemplateContentType = types.find(t => t.type === 'page_template');
  const isPageDesignMode = searchParams.get('design');

  /* get from /pages */
  useEffect(() => {
    if (pagesLoaded && pages?.length > 0) {
      const matchedPage = pages.find(
        p =>
          p.id ===
          window.location.pathname.replace('/edit-page/', '').replace('#', '')
      );

      if (!matchedPage) {
        setLastPageId(undefined);
        setIs404(true);
      } else {
        setPage(matchedPage);
      }
    }
  }, [pages, pagesLoaded, window?.location?.pathname]);

  /* load the page template if there is one */
  useEffect(() => {
    let unsubPageDataLinks = () => {};
    if (
      page?.templateId?.trim().length > 0 &&
      (pageTemplate === undefined || pageTemplate?.id !== page?.templateId)
    ) {
      getPageTemplate(page.templateId);
    }
    if (page) {
      async function loadPageDataLinks() {
        unsubPageDataLinks = onSnapshot(
          query(collection(db, `pages/${page.id}/page_datalinks`)),
          querySnapshot => {
            let items = [];
            querySnapshot.forEach(item => {
              items.push({
                id: item.id,
                ...item.data()
              });
            });
            items.sort((a, b) =>
              a.dataLinkNameSingular < b.dataLinkNameSingular ? 1 : -1
            );
            setPageDataLinks(items);
          }
        );
      }
      loadPageDataLinks();
    }
    return unsubPageDataLinks;
  }, [page]);

  /* load page revisions */
  useEffect(() => {
    let unsubRevisions = () => {};

    async function loadRevisions() {
      unsubRevisions = onSnapshot(
        query(
          collection(db, `pages/${page.id}/revisions`),
          orderBy('createdAt')
        ),
        querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });
          items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
          setPageRevisions(items);
        }
      );
    }

    if (showPageRevisions) {
      loadRevisions();
    }

    return () => {
      setPageRevisions([]);
      unsubRevisions();
    };
  }, [showPageRevisions]);

  /* load template or snippet revisions */
  useEffect(() => {
    let unsubRevisions = () => {};

    async function loadSnippetOrTemplateRevisions() {
      unsubRevisions = onSnapshot(
        query(
          collection(
            db,
            `pages_templates/${selectedSnippetOrTemplate.id}/revisions`
          ),
          orderBy('createdAt')
        ),
        querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });
          items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
          setTemplateOrSnippetRevisions(items);
        }
      );
    }

    if (showSnippetOrTemplateRevisions) {
      loadSnippetOrTemplateRevisions();
    }

    return () => {
      setTemplateOrSnippetRevisions([]);
      unsubRevisions();
    };
  }, [showSnippetOrTemplateRevisions]);

  /* load the action sequences and states for this page */
  useEffect(() => {
    // eslint-disable-next-line
    let unsubPageActionSequences = () => {};
    // eslint-disable-next-line
    let unsubPageStates = () => {};

    async function loadPageActionSequences() {
      unsubPageActionSequences = onSnapshot(
        query(
          collection(db, `pages/${page.id}/sequences`),
          where('deleted', '==', false)
        ),
        async querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });

          await Promise.all(
            items.map(async sequence => {
              const stepsSnap = await getDocs(
                collection(
                  db,
                  `pages/${page.id}/sequences/${sequence.id}/action_sequence_steps`
                ),
                where('deleted', '==', false)
              );

              sequence.steps = [];

              stepsSnap.forEach(doc => {
                sequence.steps.push({
                  id: doc.id,
                  ...doc.data()
                });
              });

              if (sequence.steps.length > 0) {
                await Promise.all(
                  sequence.steps.map(async step => {
                    step.fields = [];
                    const fieldsSnap = await getDocs(
                      collection(
                        db,
                        `pages/${page.id}/sequences/${sequence.id}/action_sequence_steps/${step.id}/action_sequence_step_fields`
                      ),
                      where('deleted', '==', false)
                    );

                    fieldsSnap.forEach(doc => {
                      step.fields.push({
                        id: doc.id,
                        ...doc.data()
                      });
                    });
                  })
                );
              }
            })
          );

          setSequences(items);
          setSequencesLoaded(true);
        }
      );
    }

    async function loadPageStates() {
      unsubPageStates = onSnapshot(
        query(
          collection(db, `pages/${page.id}/page_states`),
          where('deleted', '==', false)
        ),
        async querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });

          page.states = items;
        }
      );
    }

    if (page && (lastPageId !== page?.id || !sequencesLoaded)) {
      setLastPageId(page.id);
      loadPageActionSequences();
      loadPageStates();
    }

    return () => {
      //unsubPageActionSequences();
      //unsubPageStates();
    };
  }, [page]);

  /* load action sequences associated with a template or snippet */
  useEffect(() => {
    // eslint-disable-next-line
    let unsubTemplateActionSequences = () => {};
    // eslint-disable-next-line
    let unsubTemplateStates = () => {};

    async function loadTemplateActionSequences() {
      unsubTemplateActionSequences = onSnapshot(
        query(
          collection(
            db,
            `pages_templates/${selectedSnippetOrTemplate.id}/sequences`
          ),
          where('deleted', '==', false)
        ),
        async querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });

          await Promise.all(
            items.map(async sequence => {
              const stepsSnap = await getDocs(
                collection(
                  db,
                  `pages_templates/${selectedSnippetOrTemplate.id}/sequences/${sequence.id}/action_sequence_steps`
                )
              );

              sequence.steps = [];

              stepsSnap.forEach(doc => {
                sequence.steps.push({
                  id: doc.id,
                  ...doc.data()
                });
              });

              if (sequence.steps.length > 0) {
                await Promise.all(
                  sequence.steps.map(async step => {
                    step.fields = [];
                    const fieldsSnap = await getDocs(
                      collection(
                        db,
                        `pages_templates/${selectedSnippetOrTemplate.id}/sequences/${sequence.id}/action_sequence_steps/${step.id}/action_sequence_step_fields`
                      )
                    );

                    fieldsSnap.forEach(doc => {
                      step.fields.push({
                        id: doc.id,
                        ...doc.data()
                      });
                    });
                  })
                );
              }
            })
          );

          setSnippetOrTemplateSequences(items);
          setSnippetOrTemplateSequencesLoaded(true);
        }
      );
    }

    async function loadTemplateStates() {
      unsubTemplateStates = onSnapshot(
        query(
          collection(
            db,
            `pages_templates/${selectedSnippetOrTemplate.id}/page_states`
          )
        ),
        async querySnapshot => {
          let items = [];
          querySnapshot.forEach(item => {
            items.push({
              id: item.id,
              ...item.data()
            });
          });

          selectedSnippetOrTemplate.states = items;
        }
      );
    }

    if (
      selectedSnippetOrTemplate &&
      (lastSnippetOrTemplateId !== selectedSnippetOrTemplate?.id ||
        !snippetOrTemplateSequencesLoaded)
    ) {
      setLastSnippetOrTemplateId(selectedSnippetOrTemplate.id);
      loadTemplateActionSequences();
      loadTemplateStates();
    }

    return () => {
      //unsubTemplateActionSequences();
      //unsubTemplateStates();
    };
  }, [selectedSnippetOrTemplate]);

  const toggleLibrary = () => {
    setShowLibrary(!showLibrary);
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;

    targetWindow.postMessage({
      name: 'showLibrary',
      value: !showLibrary
    });
  };

  const toggleSnippets = () => {
    setShowSnippets(!showSnippets);
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;

    targetWindow.postMessage({
      name: 'showSnippets',
      value: !showSnippets
    });
  };

  const toggleSequences = () => {
    setShowSequences(!showSequences);
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'showActionSequences',
      value: !showSequences
    });
  };

  const onPaste = () => {
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'paste',
      value: ''
    });
  };

  const onAddEmptyComponent = () => {
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'addEmptyComponent',
      value: ''
    });
  };

  const onDataScenarioSelected = async scenario => {
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'dataScenarioSelected',
      value: scenario
    });
  };

  const onChanged = hasChanges => {
    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'designer_has_changes_status',
      value: hasChanges
    });
  };

  const togglePreview = () => {
    setPreviewPage(!previewPage);
  };

  const togglePageRevisions = () => {
    setShowPageRevisions(!showPageRevisions);
  };

  const toggleSnippetOrTemplateRevisions = () => {
    setShowSnippetOrTemplateRevisions(!showSnippetOrTemplateRevisions);
  };

  const saveTemplateRevision = async template => {
    cleanMetadata(template);

    const revision = {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email,
      components: template.components
    };

    await addDoc(
      collection(db, `pages_templates/${template.id}/revisions`),
      revision
    );

    let updatedTemplate = {
      ...template,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email,
      html:
        template.components?.length > 0
          ? template.components.map(c => c.html).join(' ')
          : ''
    };

    delete updatedTemplate.areas;

    consoleLog('saveTemplateRevision.updatingTemplate', updatedTemplate);
    await updateDoc(
      doc(db, `pages_templates/${template.id}`),
      updatedTemplate,
      template.id
    );

    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'save_complete',
      value: true
    });
  };

  const saveSnippetRevision = async snippet => {
    cleanMetadata(snippet);

    const revision = {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email,
      components: snippet.components
    };

    await addDoc(
      collection(db, `pages_templates/${snippet.id}/revisions`),
      revision
    );

    let updatedSnippet = {
      ...snippet,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email,
      html:
        snippet.components?.length > 0
          ? snippet.components.map(c => c.html).join(' ')
          : ''
    };

    consoleLog('saveSnippetRevision.updatingSnippet', snippet);
    await updateDoc(
      doc(db, `pages_templates/${snippet.id}`),
      updatedSnippet,
      snippet.id
    );

    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'save_complete',
      value: true
    });
  };

  const savePageRevision = async page => {
    cleanMetadata(page);

    const revision = {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email,
      components: page.components
    };

    await addDoc(collection(db, `pages/${page.id}/revisions`), revision);

    let updatedPage = {
      ...page,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: user.email
    };

    delete updatedPage.states;

    consoleLog('savePageRevision.updatingPage', updatedPage);
    await updateDoc(doc(db, `pages/${page.id}`), updatedPage, page.id);

    const targetWindow =
      window.location !== window.parent.location ? window.parent : window;
    targetWindow.postMessage({
      name: 'save_complete',
      value: true
    });
  };

  const cleanMetadata = document => {
    if (!document?.components?.length > 0) {
      document.metadata = {};
      return;
    }

    // cleanup metadata and remove ids that are no longer in the html
    const regex = /id="([^"]*)"/g;
    const matches = [
      ...document.components
        .map(c => c.html)
        .join(' ')
        .matchAll(regex)
    ];
    const elementIds = matches.map(match => match[1]);

    if (!document.metadata) {
      document.metadata = {};
      return;
    }

    Object.keys(document.metadata)
      .filter(key => !elementIds.find(id => id === key))
      .forEach(staleKey => {
        delete document.metadata[staleKey];
      });

    Object.keys(document.metadata).forEach(key => {
      Object.keys(document.metadata[key]).forEach(property => {
        if (
          document.metadata[key][property] === undefined ||
          ['innerHTML', 'outerHTML', 'markup', 'previousId'].some(
            s => s.toLowerCase() === property.toLowerCase()
          )
        ) {
          delete document.metadata[key][property];
        }
      });
    });

    // remove items that have no functional impact
    Object.keys(document.metadata).forEach(key => {
      if (
        !document.metadata[key].isReactComponent &&
        ['conditionType', 'action', 'validateOn'].every(
          field =>
            document.metadata[key][field] === undefined ||
            document.metadata[key][field] === '' ||
            document.metadata[key][field] === false ||
            document.metadata[key][field] === 'false'
        ) &&
        (document.metadata[key]['dataLinkField'] === undefined ||
          document.metadata[key]['dataLinkField']?.trim().length === '')
      ) {
        delete document.metadata[key];
      }
    });
  };

  /*
    Gets all available global action sequences

  */
  const getGlobalActionSequences = async ({
    metadata,
    pageLoadActionSequenceId,
    pageActionSequences
  }) => {
    const sequenceDocs = await getDocs(
      collection(db, `sequences`),
      where('deleted', '==', false),
      where('published', '==', true)
    );
    let globalActionSequences = sequenceDocs.docs
      .map(doc => ({
        ...doc.data(),
        id: doc.id
      }))
      .filter(g => !g.deleted && g.published);

    if (!Array.isArray(globalActionSequences)) {
      globalActionSequences = [];
    }

    globalActionSequences = globalActionSequences.filter(g => {
      let actionSequenceInUse = false;
      Object.keys(metadata).forEach(key => {
        if (
          metadata[key].actionSequenceId === g.id ||
          pageLoadActionSequenceId === g.id
        ) {
          actionSequenceInUse = true;
        }
      });

      if (!actionSequenceInUse) {
        // is this global sequence used within a start sequence step inside a page action sequence
        pageActionSequences.forEach(sequence => {
          if (
            sequence.steps.some(
              s => s.actionToStart === g.id && s.type === 'SEQUENCE_START'
            )
          ) {
            actionSequenceInUse = true;
          }
        });
      }

      return actionSequenceInUse;
    });

    await Promise.all(
      globalActionSequences.map(async sequence => {
        const stepsSnap = await getDocs(
          collection(db, `sequences/${sequence.id}/action_sequence_steps`),
          where('deleted', '==', false)
        );

        sequence.steps = [];

        stepsSnap.forEach(doc => {
          sequence.steps.push({
            id: doc.id,
            ...doc.data()
          });
        });

        if (sequence.steps.length > 0) {
          await Promise.all(
            sequence.steps.map(async step => {
              step.fields = [];
              const fieldsSnap = await getDocs(
                collection(
                  db,
                  `sequences/${sequence.id}/action_sequence_steps/${step.id}/action_sequence_step_fields`
                ),
                where('deleted', '==', false)
              );

              fieldsSnap.forEach(doc => {
                step.fields.push({
                  id: doc.id,
                  ...doc.data()
                });
              });
            })
          );
        }
      })
    );

    return globalActionSequences;
  };

  /* 
    pages are published to /public_page or /private_page

    We publish a page as a single json document with no subcollections

    We add nested arrays for:

    - sequences: the action sequences used on the page, including action sequences used that reference other action sequences
    - validations: the validations used on the page, derived from 'page/id/validations' subcollection
    - dataLinks: the page data links with the actual data link and content type appended
    - views: the html from the .components array organised by pageStateId
    - states: the page states and their associated functions

    How we deal with template interpolation:

    Templates are store in /pages_templates and a page may point to one via page.templateId
    Templates have a subcollection called /content_areas which specify the IDs of divs within the template that content can be placed in.
    When a component is added to a page that is using a page template, we must specify the content area.

    When we publish a page that is using a template we interpolate the components into the template html based on the content area ids
    Views (aka States) html are also interpolated in the same way

  */
  const publishPage = useCallback(
    async page => {
      consoleLog('usePageDesiger.publishPage.dataLinks', {
        props,
        dataLinks,
        dataLinksLoaded,
        storeDataLinks
      });

      let pageTemplate = await getTemplateInformation({
        templateId: page.templateId
      });

      cleanMetadata(page);

      const sequenceDocs = await getDocs(
        collection(db, `pages/${page.id}/sequences`),
        where('deleted', '==', false)
      );
      let pageActionSequences = sequenceDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      pageActionSequences = pageActionSequences.filter(a => !a.deleted);

      await Promise.all(
        pageActionSequences.map(async sequence => {
          const stepsSnap = await getDocs(
            collection(
              db,
              `pages/${page.id}/sequences/${sequence.id}/action_sequence_steps`
            ),
            where('deleted', '==', false)
          );

          sequence.steps = [];

          stepsSnap.forEach(doc => {
            sequence.steps.push({
              id: doc.id,
              ...doc.data()
            });
          });

          if (sequence.steps.length > 0) {
            await Promise.all(
              sequence.steps.map(async step => {
                step.fields = [];
                const fieldsSnap = await getDocs(
                  collection(
                    db,
                    `pages/${page.id}/sequences/${sequence.id}/action_sequence_steps/${step.id}/action_sequence_step_fields`
                  ),
                  where('deleted', '==', false)
                );

                fieldsSnap.forEach(doc => {
                  step.fields.push({
                    id: doc.id,
                    ...doc.data()
                  });
                });
              })
            );
          }
        })
      );

      const validationDocs = await getDocs(
        collection(db, `pages/${page.id}/validations`),
        where('deleted', '==', false)
      );
      const validations = validationDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const pageDataLinksDocs = await getDocs(
        collection(db, `pages/${page.id}/page_datalinks`),
        where('deleted', '==', false)
      );
      const pageDataLinks = pageDataLinksDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const pageStateDocs = await getDocs(
        collection(db, `pages/${page.id}/page_states`),
        where('deleted', '==', false)
      );
      const pageStates = pageStateDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      pageDataLinks.forEach(pageDataLink => {
        pageDataLink.dataLink = dataLinks.find(
          d => d.id === pageDataLink.dataLinkId
        );

        const dlType = types.find(t => t.type === pageDataLink.dataLink.type);
        delete dlType.detail;
        delete dlType.list;
        delete dlType.calendar;
        dlType.fields = dlType.fields.map(f => ({
          name: f.name || '',
          type: f.type || '',
          label: f.label || '',
          defaultValue: f.defaultValue || ''
        }));
        pageDataLink.dataLink.contentType = dlType;
        pageDataLink.dataLinkNameSingular = pageDataLink.dataLinkNameSingular.toLowerCase();
        pageDataLink.dataLinkNamePlural = pageDataLink.dataLinkNamePlural.toLowerCase();
      });

      pageStates.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));
      const views = pageStates
        .filter(p => !p.deleted)
        .map(pageState => ({
          id: pageState.id,
          name: pageState.name,
          appendToPage: pageState.appendToPage,
          code: pageState.functionCode
        }));

      let publishedMetaData = {
        ...(page.metadata || {})
      };

      await Promise.all(
        views.map(async view => {
          const { html, metadata } = await getTemplateHtml({
            appendToPage: view.appendToPage,
            template: pageTemplate,
            components: page.components?.filter(c => c.pageStateId === view.id),
            defaultHtml: ''
          });
          view.html = injectSquirrelly(html);
          view.metadata = metadata;
        })
      );

      const { html, metadata } = await getTemplateHtml({
        template: pageTemplate,
        components: page.components?.filter(
          c =>
            !c.pageStateId ||
            c.pageStateId?.trim().length === 0 ||
            pageStates.length === 0
        ),
        defaultHtml: 'No content is available for this page'
      });

      const pageHtml = html;

      publishedMetaData = {
        ...(pageTemplate?.metadata || {}),
        ...publishedMetaData,
        ...(metadata || {})
      };

      let pageCss = page?.css || '';
      if (
        settings?.length > 0 &&
        settings[0]?.pagesGlobalCss?.trim().length > 0
      ) {
        pageCss += settings[0].pagesGlobalCss;
      }

      let globalActionSequences = await getGlobalActionSequences({
        metadata: publishedMetaData,
        pageLoadActionSequenceId: page.onLoadSequenceId,
        pageActionSequences
      });

      const publishedPage = {
        id: page.id,
        name: page.name,
        templateId: page?.templateId || '',
        path: page.path,
        html: injectSquirrelly(pageHtml),
        css: pageCss,
        metadata: publishedMetaData,
        onLoadSequenceId: page.onLoadSequenceId || '',
        sequences: [
          ...pageActionSequences,
          ...(pageTemplate?.sequences || []),
          ...globalActionSequences
        ],
        validations: [...validations, ...(pageTemplate?.validations || [])],
        dataLinks: [
          ...(pageDataLinks ? pageDataLinks : []),
          ...(pageTemplate?.dataLinks || [])
        ],
        dataLinksFunction: page.dataLinksFunction,
        views: [...views, ...(pageTemplate?.states || [])],
        publicAccess: page.publicAccess,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.email
      };

      if (!page.publicAccess) {
        await deleteDoc(doc(db, 'pages_public', publishedPage.id));
      }

      await setDoc(
        doc(
          db,
          page.publicAccess ? 'pages_public' : 'pages_private',
          publishedPage.id
        ),
        publishedPage
      );

      await setDoc(
        doc(db, 'pages', publishedPage.id),
        {
          updatedAt: serverTimestamp(),
          lastUpdatedBy: user.email,
          published: true
        },
        { merge: true }
      );

      await updateSiteMaps({
        publishedPageId: publishedPage.id
      });

      const targetWindow =
        window.location !== window.parent.location ? window.parent : window;
      targetWindow.postMessage({
        name: 'publish_complete',
        value: true
      });
    },
    [dataLinks, storeDataLinks, dataLinksLoaded, pageTemplate, page, pages]
  );

  const getPageTemplate = async templateId => {
    const pageTemplate = await getTemplateInformation({ templateId });
    setPageTemplate(pageTemplate);

    /*const docRef = doc(db, 'pages_templates', templateId);

    return onSnapshot(docRef, async doc => {
      const temp = doc.data();
      const q = query(
        collection(db, `pages_templates/${templateId}/content_areas`)
      );
      const querySnapshot = await getDocs(q);
      temp.areas = [];
      querySnapshot.forEach(doc => {
        temp.areas.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setPageTemplate(temp);
    });*/
  };

  const getTemplateInformation = async ({ templateId }) => {
    if (!templateId || templateId.trim().length === 0) {
      return;
    }

    let template = {};
    template.dataLinks = [];
    template.validations = [];
    template.states = [];
    template.sequences = [];

    // have to re-get the template here as  even using useCallback with pageTemplate as dependency
    // does not give us access to pageTemplate that we loaded when the hook was loaded.
    const docRef = doc(db, 'pages_templates', templateId);
    const templateSnap = await getDoc(docRef);
    if (templateSnap.exists()) {
      template = {
        ...templateSnap.data(),
        ...template
      };

      const q = query(
        collection(db, `pages_templates/${templateId}/content_areas`)
      );
      const querySnapshot = await getDocs(q);
      template.areas = [];
      querySnapshot.forEach(doc => {
        template.areas.push({
          id: doc.id,
          ...doc.data()
        });
      });

      const sequenceDocs = await getDocs(
        collection(db, `pages_templates/${templateId}/sequences`),
        where('deleted', '==', false)
      );
      template.sequences = sequenceDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      await Promise.all(
        template.sequences?.map(async sequence => {
          const stepsSnap = await getDocs(
            collection(
              db,
              `pages_templates/${templateId}/sequences/${sequence.id}/action_sequence_steps`
            ),
            where('deleted', '==', false)
          );

          sequence.steps = [];

          stepsSnap.forEach(doc => {
            sequence.steps.push({
              id: doc.id,
              ...doc.data()
            });
          });

          if (sequence.steps.length > 0) {
            await Promise.all(
              sequence.steps.map(async step => {
                step.fields = [];
                const fieldsSnap = await getDocs(
                  collection(
                    db,
                    `pages_templates/${templateId}/sequences/${sequence.id}/action_sequence_steps/${step.id}/action_sequence_step_fields`
                  ),
                  where('deleted', '==', false)
                );

                fieldsSnap.forEach(doc => {
                  step.fields.push({
                    id: doc.id,
                    ...doc.data()
                  });
                });
              })
            );
          }
        })
      );

      const validationDocs = await getDocs(
        collection(db, `pages_templates/${templateId}/validations`),
        where('deleted', '==', false)
      );

      template.validations = validationDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const templateDataLinksDocs = await getDocs(
        collection(db, `pages_templates/${templateId}/page_datalinks`),
        where('deleted', '==', false)
      );
      template.dataLinks = templateDataLinksDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const templateStateDocs = await getDocs(
        collection(db, `pages_templates/${templateId}/page_states`),
        where('deleted', '==', false)
      );

      template.states = templateStateDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      template.dataLinks.forEach(templateDataLink => {
        templateDataLink.dataLink = dataLinks.find(
          d => d.id === templateDataLink.dataLinkId
        );
        templateDataLink.dataLink.contentType = types.find(
          t => t.type === templateDataLink.dataLink.type
        );
        templateDataLink.dataLinkNameSingular = templateDataLink.dataLinkNameSingular.toLowerCase();
        templateDataLink.dataLinkNamePlural = templateDataLink.dataLinkNamePlural.toLowerCase();
      });

      template.states.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));
      template.states = template.states
        .filter(p => !p.deleted)
        .map(templateState => ({
          id: templateState.id,
          name: templateState.name,
          appendToPage: templateState.appendToPage,
          code: templateState.functionCode
        }));

      /*
      let publishedMetaData = {
        ...(page.metadata || {})
      };
      */
      template.states.map(async view => {
        view.html = template.components
          ?.filter(c => c.pageStateId === view.id)
          .map(c => c.html)
          .join(' ');
      });
    } else {
      return;
    }

    return template;
  };

  const getTemplateHtml = async ({
    appendToPage,
    template,
    components,
    defaultHtml = ''
  }) => {
    if (!components || components.length === 0) {
      return {
        html: defaultHtml,
        metadata: {}
      };
    } else if (!template || appendToPage) {
      return {
        html: components.map(c => c.html).join(' '),
        metadata: {}
      };
    }

    let html;

    let metadata = {};

    if (
      template &&
      template.areas.some(area =>
        components.some(c => c.templateAreaName === area.name)
      )
    ) {
      /*
        if the template has content areas that match are found in the supplied components,
        then generate the html for the components applicable to each of these areas and substitute it in
      */
      html = template.html;
      metadata = template.metadata || {};

      template.areas.forEach(area => {
        // eslint-disable-next-line
        const regex = new RegExp(
          `<div id="${area.name}"[^>]*>(.*?)</div>`,
          's'
        );
        const componentsInArea = components.filter(
          c => c.templateAreaName === area.name
        );
        if (componentsInArea.length > 0) {
          const componentHtml = componentsInArea.map(c => c.html).join(' ');
          html = html.replace(regex, componentHtml);
        }
      });

      if (template.css?.trim().length > 0) {
        html += `<style type="text/css">${template.css}</style>`;
      }
    } else {
      html = components.map(c => c.html).join(' ');
    }

    return {
      html,
      metadata
    };
  };

  /*
    The portal needs to know what pages are available to the user in order to setup routing,
    so we build a sitemap document for authenticated and unauthenticated users so we 
    can build the router without having to load every document from either the public or the pages
    collection.
  */
  const updateSiteMaps = useCallback(
    async ({ unpublishedPageId, publishedPageId }) => {
      const pagesDocs = await getDocs(collection(db, 'pages'));
      const pages = pagesDocs.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      await Promise.all(
        pages.map(async page => {
          const stepsSnapshot = await getDocs(
            collection(db, `pages/${page.id}/page_roles`)
          );
          let roles = [];
          stepsSnapshot.forEach(doc => {
            roles.push({
              id: doc.id,
              ...doc.data()
            });
          });
          page.roles = roles;
        })
      );

      // we pass in unpublishedPageId or unpublishedPageId in case redux has not yet updated the published status of pages in the pages redux
      const privatePages = pages
        .filter(
          p =>
            !p.publicAccess &&
            (p.published || p.id === publishedPageId) &&
            p.id !== unpublishedPageId
        )
        .map(p => ({
          name: p.name,
          path: p.path,
          id: p.id,
          showDividerAfter: p.sidebarShowDividerAfter
            ? p.sidebarShowDividerAfter
            : false,
          showSidebar: p.showSidebar === undefined ? false : p.showSidebar,
          sidebarIcon: p.sidebarIcon,
          showInSidebar:
            p.showInSidebar === undefined ? false : p.showInSidebar,
          listen: p.listen !== undefined ? p.listen : false,
          roles: p.roles?.map(r => r.role) || []
        }));

      const docRefPrivate = doc(db, 'sitemaps', 'private');
      const docSnapPrivate = await getDoc(docRefPrivate);

      if (docSnapPrivate.exists()) {
        await updateDoc(docRefPrivate, {
          updatedAt: serverTimestamp(),
          pages: privatePages
        });
      } else {
        await setDoc(doc(db, 'sitemaps', 'private'), {
          updatedAt: serverTimestamp(),
          pages: privatePages
        });
      }

      const publicPages = pages
        .filter(
          p =>
            p.publicAccess &&
            (p.published || p.id === publishedPageId) &&
            p.id !== unpublishedPageId
        )
        .map(p => ({
          name: p.name,
          path: p.path,
          id: p.id,
          showSidebar: p.showSidebar === undefined ? false : p.showSidebar,
          showInSidebar:
            p.showInSidebar === undefined ? false : p.showInSidebar,
          sidebarIcon: p.sidebarIcon || 'DocumentIcon',
          sidebarTitle: p.sidebarTitle || p.name,
          showDividerAfter:
            p.sidebarShowDividerAfter === undefined
              ? false
              : p.sidebarShowDividerAfter,
          listen: p.listen !== undefined ? p.listen : false
        }));

      const docRefPublic = doc(db, 'sitemaps', 'public');
      const docSnapPublic = await getDoc(docRefPublic);

      if (docSnapPublic.exists()) {
        await updateDoc(docRefPublic, {
          updatedAt: serverTimestamp(),
          pages: publicPages
        });
      } else {
        await setDoc(doc(db, 'sitemaps', 'public'), {
          updatedAt: serverTimestamp(),
          pages: publicPages
        });
      }
    },
    [pages, pagesLoaded]
  );

  useEffect(() => {
    window.addEventListener('message', event => {
      if (event.data?.name === 'showLibrary') {
        setShowLibrary(event.data.value);
      } else if (event.data?.name === 'previewPage') {
        setPreviewPage(event.data.value);
      } else if (event.data?.name === 'showPageRevisions') {
        setShowPageRevisions(event.data.value);
      }
    });
  }, []);

  const unpublishPage = async pageId => {
    await setDoc(
      doc(db, 'pages', pageId),
      {
        published: false,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user.email
      },
      { merge: true }
    );
    await updateSiteMaps({
      unpublishedPageId: pageId
    });
  };

  const selectSnippetOrTemplate = async inbound => {
    /*const newSelection = await getTemplateInformation({
      templateId: inbound.id
    });*/
    setSelectedSnippetOrTemplate(inbound);
  };

  return {
    is404,
    page,
    sequences,
    pageDataLinks,
    pageTemplate,
    pageContentType,
    pageTemplateContentType,
    isPageDesignMode,
    toggleLibrary,
    toggleSnippets,
    toggleSequences,
    togglePreview,
    togglePageRevisions,
    toggleSnippetOrTemplateRevisions,
    showLibrary,
    showSnippets,
    previewPage,
    showPageRevisions,
    showSnippetOrTemplateRevisions,
    savePageRevision,
    saveTemplateRevision,
    saveSnippetRevision,
    publishPage,
    unpublishPage,
    pageRevisions,
    templateOrSnippetRevisions,
    onPaste,
    onAddEmptyComponent,
    onDataScenarioSelected,
    onChanged,
    onSnippetOrTemplateSelected: selectSnippetOrTemplate,
    selectedSnippetOrTemplate,
    snippetOrTemplateSequences,
    data: {
      dataLinks,
      dataLinksLoaded,
      storeDataLinks
    }
  };
};

const useSiteMap = () => {
  const [user, loading] = useAuthState(auth);

  const [publicSiteMap, setPublicSiteMap] = useState();
  const [privateSiteMap, setPrivateSiteMap] = useState();
  const [siteMap, setSiteMap] = useState();

  const loadPrivateSiteMap = async listen => {
    const docRef = doc(db, 'sitemaps', 'private');

    if (listen) {
      consoleLog('useSiteMap.listening to private sitemap');
      setPrivateSiteMap([]);
      return onSnapshot(docRef, doc => {
        const sitemap = doc.data();
        if (Array.isArray(sitemap?.pages)) {
          setPrivateSiteMap(sitemap.pages);
        }
      });
    } else {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const sitemap = docSnap.data();
        if (Array.isArray(sitemap?.pages)) {
          setPrivateSiteMap(sitemap.pages);
        }
      } else {
        setPrivateSiteMap([]);
      }
    }
  };

  /*
    If we are logged in we want to be able to 
    see sitemap changes instantly in the sidebar. We don't really want
    to have a snapshot listener open for every logged in user due to cost
    but we are at the moment
  */

  useEffect(() => {
    if (!loading && user) {
      loadPrivateSiteMap(user?.uid !== undefined);
    }
  }, [user]);

  const loadPublicSiteMap = async listen => {
    const docRef = doc(db, 'sitemaps', 'public');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const sitemap = docSnap.data();
      if (Array.isArray(sitemap?.pages)) {
        setPublicSiteMap(
          sitemap.pages.map(p => ({
            ...p,
            publicAccess: true
          }))
        );
      }
    } else {
      setPublicSiteMap([]);
    }

    if (listen) {
      consoleLog('useSiteMap.listening to public sitemap');
      return onSnapshot(docRef, doc => {
        const sitemap = doc.data();
        if (Array.isArray(sitemap?.pages)) {
          setPublicSiteMap(
            sitemap.pages.map(p => ({
              ...p,
              publicAccess: true
            }))
          );
        }
      });
    }
  };

  useEffect(() => {
    loadPublicSiteMap(user?.uid !== undefined);
  }, [user]);

  useEffect(() => {
    let items = [];
    if (Array.isArray(publicSiteMap)) {
      items = [...publicSiteMap];
    }
    if (Array.isArray(privateSiteMap)) {
      items = [...items, ...privateSiteMap];
    }
    setSiteMap(items);
    /*if (items.length > 0) {
      setSiteMap(items);
    } else {
      setSiteMap(undefined);
    }*/
  }, [publicSiteMap, privateSiteMap]);

  return {
    siteMap
  };
};

/* 
    pages can have the same paths but can be set for publicAccess or set for private access but 
      with different roles specified, so we must determine which pages to build into the router
      for the current user. The sidebar also uses this hook to pull out user specific pages
*/
const useUserSiteMap = ({ siteMap }) => {
  const { ready } = useAuthenticated('useUserSiteMap');

  const user = useSelector(state => state.User);

  /* build an array of pages applicable to the current user */
  const userPages = useMemo(() => {
    const myPages = [];

    const uniqueUrls = Array.from(new Set(siteMap.map(s => s.path)));

    uniqueUrls.forEach(path => {
      const matchingPages = siteMap.filter(s => s.path === path);
      if (matchingPages.length === 0) {
        // this path is only used on a single page
        myPages.push(matchingPages[0]);
      } else {
        // this path is used on multiple pages
        const pagesWithRoles = matchingPages.filter(
          p => p.roles?.length > 0 && p.roles.some(r => r === user?.role)
        );
        if (pagesWithRoles.length === 1) {
          // exactly one matching page for the users current role
          myPages.push(pagesWithRoles[0]);
        } else if (pagesWithRoles.length > 1) {
          // same path with same roles resulting in multiple matches
          myPages.push({ ...pagesWithRoles[0], pageConfigurationError: true });
        } else {
          // exclude pages that have roles specified but don't match the user role
          let nonRolePages = matchingPages.filter(
            p => !(p.roles?.length > 0 && !p.roles.some(r => r === user?.role))
          );

          if (nonRolePages.length === 1) {
            myPages.push(nonRolePages[0]);
          } else if (nonRolePages.length > 1) {
            // try and exclude public pages
            if (nonRolePages.filter(p => !p.publicAccess).length === 1) {
              myPages.push(nonRolePages.filter(p => !p.publicAccess)[0]);
            } else {
              // multiple pages match that don't use roles which is a configuration error
              myPages.push({
                ...nonRolePages[0],
                pageConfigurationError: true
              });
            }
          }
        }
      }
    });

    return myPages;
  }, [siteMap, user, ready]);

  consoleLog('useUserSiteMap.userPages', userPages);

  return {
    userPages,
    ready
  };
};

export { usePageDesigner, useSiteMap, useUserSiteMap };
